import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, startOfWeek, isSameDay, isAfter, parseISO, startOfDay } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EventDetailModal from './events/EventDetailModal';
import { supabase } from '../supabase';

const { FiCalendar, FiChevronLeft, FiChevronRight, FiLoader } = FiIcons;

const Events = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('events_1740480000000')
        .select('*')
        .order('event_date', { ascending: true });

      if (data) {
        setEvents(data);
        // Find the next available event starting from today
        const today = startOfDay(new Date());
        const nextEvent = data.find(event => {
          const eventDate = parseISO(event.event_date);
          return isSameDay(eventDate, today) || isAfter(eventDate, today);
        });

        // If a future event exists, set the calendar to that week
        if (nextEvent) {
          setCurrentDate(parseISO(nextEvent.event_date));
        }
      }
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  const days = Array.from({ length: 7 }, (_, i) =>
    addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i)
  );

  return (
    <section id="events" className="py-24 bg-gray-50/50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Upcoming Events
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            Join us for a guided mindfulness and creative coaching session.
          </p>
        </div>

        <div className="flex items-center justify-between mb-10 bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100">
          <button
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
            className="p-3 hover:bg-rose-50 rounded-full transition-colors text-rose-500"
          >
            <SafeIcon icon={FiChevronLeft} className="w-8 h-8" />
          </button>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800">{format(currentDate, 'MMMM yyyy')}</h3>
            <p className="text-xs text-rose-400 uppercase tracking-widest font-black mt-1">
              Week of {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')}
            </p>
          </div>
          <button
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
            className="p-3 hover:bg-rose-50 rounded-full transition-colors text-rose-500"
          >
            <SafeIcon icon={FiChevronRight} className="w-8 h-8" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <SafeIcon icon={FiLoader} className="w-12 h-12 text-rose-500 animate-spin mb-4" />
            <p className="text-gray-500 font-medium tracking-wide">Fetching the calendar...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
            {days.map((day, idx) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const dayEvents = events.filter(e => e.event_date === dayStr);
              const isToday = isSameDay(day, new Date());

              return (
                <div
                  key={idx}
                  className={`relative rounded-[2.5rem] p-8 min-h-[320px] transition-all border ${
                    isToday
                      ? 'bg-white border-rose-200 shadow-2xl ring-1 ring-rose-100'
                      : 'bg-white/50 border-gray-100 hover:bg-white hover:shadow-xl'
                  }`}
                >
                  {isToday && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-rose-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest">
                      Today
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <div className={`text-xs font-black uppercase tracking-[0.2em] mb-2 ${isToday ? 'text-rose-400' : 'text-gray-300'}`}>
                      {format(day, 'EEE')}
                    </div>
                    <div className={`text-4xl font-black ${isToday ? 'text-rose-600' : 'text-gray-800'}`}>
                      {format(day, 'd')}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {dayEvents.map(event => (
                      <motion.div
                        key={event.id}
                        whileHover={{ y: -3, scale: 1.05 }}
                        onClick={() => setSelectedEvent(event)}
                        className={`p-4 rounded-2xl text-white cursor-pointer shadow-lg transition-all ${
                          event.event_type === 'workshop'
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                            : 'bg-gradient-to-br from-rose-400 to-pink-500'
                        }`}
                      >
                        <div className="font-bold text-xs line-clamp-2">{event.title}</div>
                        <div className="text-[10px] font-medium mt-2 opacity-90">{event.start_time}</div>
                      </motion.div>
                    ))}
                    {dayEvents.length === 0 && (
                      <div className="flex flex-col items-center justify-center pt-10 opacity-10">
                        <SafeIcon icon={FiCalendar} className="w-10 h-10" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onBook={() => {
              setSelectedEvent(null);
              document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Events;